import { Component } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ModalBaseComponent, TranslatedToastrService, ValidationHandlerService } from 'lib/Vestra.shared';
import { BsModalService } from 'ngx-bootstrap/modal';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { CoreService, EmailResourceSaveModel, EmailTokenListModel, EmailType, ResourceModel, SendEmailModel } from 'src/app/services/api-services';
import { HttpErrorResponse } from '@angular/common/http';
import { NgxSpinnerService } from 'ngx-spinner';
import beautify from 'js-beautify';
declare var $: any;
@Component({
    selector: 'app-email-edit',
    templateUrl: './email-edit.component.html',
    styleUrls: ['./email-edit.component.scss'],
})
export class EmailEditComponent extends ModalBaseComponent {
    showModal: boolean = true;
    isSMSVisible: boolean = false;
    emailType: EmailType;

    get isAffiliateEmailType(): boolean {
        return this.emailType === EmailType.AffiliateLink;
    }
    get isAuthEmailType(): boolean {
        return this.emailType === EmailType.ForgotPassword || this.emailType === EmailType.ResetPassword || this.emailType === EmailType.PasswordExpiration || this.emailType === EmailType.FirstLogin;
    }

    constructor(
        private coreService: CoreService,
        protected override validationHandlerService: ValidationHandlerService,
        protected override modalService: BsModalService,
        private translatedToastrService: TranslatedToastrService,
        protected override spinnerService: NgxSpinnerService
    ) {
        super(spinnerService, modalService, validationHandlerService);
    }

    public title: string;
    public item: ResourceModel;

    public subject: ResourceModel;
    public body: ResourceModel;
    public smsBody: ResourceModel;

    public tokensList: EmailTokenListModel;

    private getFormGroup(): FormGroup {
        return new FormGroup({
            language: new FormControl({ value: '', disabled: true }),
            subjectKey: new FormControl({ value: '', disabled: true }),
            subjectValue: new FormControl(''),
            bodyKey: new FormControl({ value: '', disabled: true }),
            bodyValue: new FormControl('', [Validators.required]),
            smsBodyKey: new FormControl({ value: '', disabled: true }),
            smsBodyValue: new FormControl(''),
        });
    }

    private getTestEmailFormGroup(): FormGroup {
        return new FormGroup({
            toEmail: new FormControl('', Validators.required),
        });
    }

    public form: FormGroup = this.getFormGroup();
    public testEmailForm: FormGroup = this.getTestEmailFormGroup();

    public ngOnInit() {
        if (this.item) {
            this.subject = this.item;
            this.getEmailBody();
        }
    }

    ngAfterViewInit(): void {
        this.initSummerNote();
    }

    public async initForm() {
        this.form = this.getFormGroup();
        this.setFormValues();

        this.testEmailForm = this.getTestEmailFormGroup();
    }

    public initSummerNote() {
        $('#summernote').summernote({
            height: 500,
            tooltip: true,
            tabsize: 2,
            toolbar: [
                ['style', ['style']],
                ['font', ['bold', 'italic', 'underline', 'clear']],
                ['fontname', ['fontname']],
                ['color', ['color']],
                ['para', ['ul', 'ol', 'paragraph']],
                ['insert', ['link', 'picture', 'video', 'table']],
                ['view', ['codeview', 'help']],
            ],
            callbacks: {
                onChange: (contents: string) => {
                    this.form.controls['bodyValue'].setValue(contents);
                },
                onBlurCodeview: () => {
                    let codeContents = $('#summernote').summernote('code');
                    this.form.controls['bodyValue'].setValue(codeContents);
                },
            },
        });

        this.setSummerNoteValue();
    }

    public getAllTokens = async (): Promise<any> => {
        this.handleSpinner(
            firstValueFrom(this.coreService.getAllEmailTokens())
                .then(res => {
                    this.tokensList = res.result;
                    return Promise.resolve(res);
                })
                .catch((err: HttpErrorResponse) => {
                    this.validationHandlerService.handleServerError(err);
                })
        );
    };

    public copyToClipboard(value: string) {
        navigator.clipboard
            .writeText(value)
            .then(() => {
                this.translatedToastrService.success('common.success_copy', 'common.success', {}, value);
            })
            .catch(err => {
                this.validationHandlerService.handleServerError(err);
            });
    }

    public onSave = async (): Promise<any> => {
        if (this.validationHandlerService.checkFormValid(this.form)) {
            if ($('.note-editable')[0].innerText != '' && $('.note-editable')[0].innerText != '\n') {
                this.checkAndSaveBodyValue(this.form.get('bodyValue')?.value);
                const formValues = this.form.getRawValue();

                let itemToSave: EmailResourceSaveModel = {
                    subjectId: this.subject.id,
                    subjectKey: formValues.subjectKey,
                    subjectValue: formValues.subjectValue,
                    bodyId: this.body.id,
                    bodyKey: formValues.bodyKey,
                    bodyValue: this.cleanHtml(formValues.bodyValue),
                    smsBodyId: this.smsBody.id,
                    smsBodyKey: formValues.smsBodyKey,
                    smsBodyValue: formValues.smsBodyValue,
                };
                return this.handleSpinner(
                    firstValueFrom(this.coreService.updateEmailResource(itemToSave))
                        .then(res => {
                            return Promise.resolve(res);
                        })
                        .catch((err: HttpErrorResponse) => {
                            this.validationHandlerService.handleServerError(err);
                        })
                );
            } else {
                this.translatedToastrService.warning('validation.html_body_value_is_required', 'common.warning');
            }
        }
    };

    public sendEmailByType = async (): Promise<any> => {
        if (this.validationHandlerService.checkFormValid(this.testEmailForm)) {
            const formValues = this.testEmailForm.getRawValue();

            let model: SendEmailModel = {
                email: formValues.toEmail,
                emailType: this.emailType,
            };

            return this.handleSpinner(
                firstValueFrom(this.coreService.sendEmailByType(model))
                    .then(res => {
                        this.testEmailForm.reset();
                        return Promise.resolve(res);
                    })
                    .catch((err: HttpErrorResponse) => {
                        this.validationHandlerService.handleServerError(err);
                    })
            );
        }
    };

    private setSummerNoteValue() {
        let formattedHtml = beautify.html(this.body?.value, {
            indent_size: 2,
            preserve_newlines: true,
            wrap_line_length: 120,
        });

        $('#summernote').summernote('code', formattedHtml);
    }

    private setFormValues() {
        this.form.setValue({
            language: this.subject.language,
            subjectKey: this.subject.key,
            subjectValue: this.subject.value,
            bodyKey: this.body.key,
            bodyValue: this.body.value,
            smsBodyKey: this.smsBody.key,
            smsBodyValue: this.smsBody.value,
        });
    }

    private getEmailBody() {
        this.handleSpinner(
            firstValueFrom(this.coreService.getEmailBody(this.item.id))
                .then(res => {
                    this.body = res.result.body;
                    this.smsBody = res.result.smsBody;
                    this.setSummerNoteValue();

                    this.initForm();
                    this.getEmailType(this.subject.key);
                    return Promise.resolve(res);
                })
                .catch((err: HttpErrorResponse) => {
                    this.validationHandlerService.handleServerError(err);
                })
        );
    }

    private checkAndSaveBodyValue(bodyValue: string) {
        const containsDoctype = bodyValue.includes('<!DOCTYPE html>');
        const containsMetaTags = bodyValue.includes('<meta charset="utf-8"');

        let cleanedBody = bodyValue;
        if (containsMetaTags) {
            cleanedBody = cleanedBody.replace(/<meta[^>]*>/gi, '');
        }

        if (!containsDoctype) {
            let updatedBodyValue = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="utf-8" />
                    <meta http-equiv="x-ua-compatible" content="IE=edge">
                    <meta name="viewport" content="width=device-width, initial-scale=1.0">
                </head>
                <body>
                    ${cleanedBody}
                </body>
                </html>
            `;

            this.form.controls['bodyValue'].setValue(updatedBodyValue);
        }
    }

    private cleanHtml(bodyValue: string): string {
        let cleanedBodyValue = bodyValue
            .replace(/\n+/g, '')
            .replace(/\r+/g, '')
            .replace(/\s{2,}/g, ' ')
            .trim();

        return cleanedBodyValue;
    }

    private getEmailType(subject: string) {
        switch (subject) {
            case 'email.forgot_password_subject':
                this.emailType = EmailType.ForgotPassword;
                break;
            case 'email.reset_password_token_subject':
                this.emailType = EmailType.ResetPassword;
                break;
            case 'email.user_first_login_subject':
                this.emailType = EmailType.FirstLogin;
                break;
            case 'email.password_expiration_warning_subject':
                this.emailType = EmailType.PasswordExpiration;
                break;
            case 'email.affiliate_link_subject':
                this.emailType = EmailType.AffiliateLink;
                break;
        }

        this.getAllTokens();
    }
}
